import { NativeDateAdapter } from '@angular/material/core';
import { Injectable } from '@angular/core';
import { DATE_INPUT_FORMAT } from '@app/utils/constants/date-input-format';
import { DAY_FIRST_DATE_LANGUAGES } from '@app/utils/constants/day-first-date-languages';
import { SupportedLanguage } from '@app/utils/constants/supported-languages';

// Thai's CLDR default calendar is Buddhist, so NativeDateAdapter (via Intl.DateTimeFormat)
// renders Buddhist-era years in the Material datepicker (e.g. 2569 instead of 2026). Force the
// Gregorian calendar via the BCP-47 `-u-ca-gregory` extension.
const THAI_GREGORIAN_LOCALE = 'th-u-ca-gregory';

@Injectable({
  providedIn: 'root',
})
export class CustomDateAdapter extends NativeDateAdapter {
  // The app language as set, before the Thai override: picks the date input's day/month order.
  private language: string = SupportedLanguage.En;

  override setLocale(locale: string): void {
    this.language = locale;
    super.setLocale(locale === SupportedLanguage.Th ? THAI_GREGORIAN_LOCALE : locale);
  }

  override format(date: Date, displayFormat: object | string): string {
    if (displayFormat !== DATE_INPUT_FORMAT) {
      return super.format(date, displayFormat as object);
    }
    const year = String(this.getYear(date)).padStart(4, '0');
    const month = String(this.getMonth(date) + 1).padStart(2, '0');
    const day = String(this.getDate(date)).padStart(2, '0');
    return this.isDayFirst() ? `${day}-${month}-${year}` : `${year}-${month}-${day}`;
  }

  override parse(value: unknown, parseFormat: unknown): Date | null {
    if (parseFormat !== DATE_INPUT_FORMAT) {
      return super.parse(value, parseFormat);
    }
    return this.parseDateInput(value);
  }

  override getFirstDayOfWeek(): number {
    switch (this.locale) {
      case SupportedLanguage.En:
      case SupportedLanguage.Id:
      case SupportedLanguage.Ko:
      case SupportedLanguage.Pt:
      case THAI_GREGORIAN_LOCALE:
      case SupportedLanguage.ZhHant:
        return 0; // Sunday
      case SupportedLanguage.Es:
      case SupportedLanguage.Ja:
      case SupportedLanguage.Vi:
      case SupportedLanguage.ZhHans:
      default:
        return 1; // Monday
    }
  }

  private isDayFirst(): boolean {
    return DAY_FIRST_DATE_LANGUAGES.includes(this.language);
  }

  // Any separator (`30-09-2026`, `30/9/2026`, `30.09.2026`) or 8 plain digits (`30092026`), in the
  // language's order. The year must have 4 digits, so `30-09-26` isn't read as the year 26. Empty text
  // is no date; anything else that isn't a real date is invalid, which the datepicker flags as
  // `matDatepickerParse`.
  private parseDateInput(value: unknown): Date | null {
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }
    const text = value.trim();
    const parts = /^\d{8}$/.test(text)
      ? this.isDayFirst()
        ? [text.slice(0, 2), text.slice(2, 4), text.slice(4)]
        : [text.slice(0, 4), text.slice(4, 6), text.slice(6)]
      : text.split(/\D+/);
    if (parts.length !== 3 || parts.some(part => !/^\d+$/.test(part))) {
      return this.invalid();
    }
    const [yearPart, monthPart, dayPart] = this.isDayFirst() ? [parts[2], parts[1], parts[0]] : parts;
    const [year, month, day] = [yearPart, monthPart, dayPart].map(Number);
    if (yearPart.length !== 4 || month < 1 || month > 12 || day < 1) {
      return this.invalid();
    }
    // Checked before `createDate`, which throws on a day the month doesn't have.
    if (day > this.getNumDaysInMonth(this.createDate(year, month - 1, 1))) {
      return this.invalid();
    }
    return this.createDate(year, month - 1, day);
  }
}
